import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { Target, Activity, Users, TrendingUp } from 'lucide-react';
interface HallmarkSlide2Props {
  isDark?: boolean;
}
export function HallmarkSlide2({
  isDark = false
}: HallmarkSlide2Props) {
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.12,
        delayChildren: 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: [0.25, 0.1, 0.25, 1]
      }
    }
  };
  return <motion.div className="h-full w-full overflow-y-auto overflow-x-hidden" variants={containerVariants} initial="hidden" animate="visible" data-id="element-23">
      <div className="min-h-full flex flex-col justify-center max-w-6xl mx-auto px-6 sm:px-12 py-20 sm:py-24" data-id="element-24">
        {/* Header */}
        <motion.div variants={itemVariants} className="text-center mb-12 sm:mb-16" data-id="element-25">
          <h1 className={`text-3xl sm:text-5xl font-serif font-medium mb-4 sm:mb-6 tracking-tight ${isDark ? 'text-white' : 'text-heliona-charcoal'}`} data-id="element-26">
            Our North Star
          </h1>
          <p className={`text-base sm:text-xl max-w-3xl mx-auto leading-relaxed ${isDark ? 'text-white/80' : 'text-heliona-text-gray'}`} data-id="element-27">
            Support Hallmark leaders during a high-change period by investing in
            their wellbeing
          </p>
        </motion.div>

        {/* Core Pillars */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8 mb-16" data-id="element-28">
          {[{
          icon: Activity,
          title: 'Measure Strain',
          desc: 'Surfacing leadership strain early through the 4C Index',
          color: 'mint'
        }, {
          icon: Target,
          title: 'Activate Support',
          desc: 'Activating targeted coaching and learning aligned to real patterns',
          color: 'orange'
        }, {
          icon: Users,
          title: 'Actionable Insight',
          desc: 'Giving HR actionable insight into leadership capacity and wellbeing',
          color: 'navy'
        }].map((item, idx) => <motion.div key={idx} variants={itemVariants} className={`rounded-3xl p-8 text-center shadow-sm hover:shadow-md transition-shadow duration-300 ${isDark ? 'bg-white/[0.03] border border-white/10' : 'bg-white border border-heliona-beige'}`} data-id="element-29">
              <div className={`w-16 h-16 rounded-2xl flex items-center justify-center mb-6 mx-auto border ${isDark ? `bg-heliona-${item.color}/10 border-heliona-${item.color}/30` : `bg-heliona-${item.color}/10 border-heliona-${item.color}/20`}`} data-id="element-30">
                <item.icon className={`w-8 h-8 ${isDark ? `text-heliona-${item.color}` : `text-heliona-${item.color === 'mint' ? 'mint-dark' : item.color}`}`} data-id="element-31" />
              </div>
              <h3 className={`text-lg sm:text-xl font-bold mb-3 ${isDark ? 'text-white' : 'text-heliona-charcoal'}`} data-id="element-32">
                {item.title}
              </h3>
              <p className={`text-sm sm:text-base leading-relaxed ${isDark ? 'text-white/70' : 'text-heliona-text-gray'}`} data-id="element-33">
                {item.desc}
              </p>
            </motion.div>)}
        </div>

        {/* Goal */}
        <motion.div variants={itemVariants} className={`rounded-3xl p-8 sm:p-10 text-center shadow-sm max-w-4xl mx-auto w-full ${isDark ? 'bg-heliona-mint/10 border border-heliona-mint/30' : 'bg-heliona-pastel-mint border border-heliona-mint/30'}`} data-id="element-34">
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-6" data-id="element-35">
            <div className={`p-3 rounded-full ${isDark ? 'bg-heliona-mint/20' : 'bg-white'}`} data-id="element-36">
              <TrendingUp className={`w-6 h-6 ${isDark ? 'text-heliona-mint' : 'text-heliona-mint-dark'}`} data-id="element-37" />
            </div>
            <div data-id="element-38">
              <h3 className={`text-lg sm:text-xl font-bold mb-1 ${isDark ? 'text-white' : 'text-heliona-charcoal'}`} data-id="element-39">
                Goal by Day 90
              </h3>
              <p className={`text-base sm:text-lg font-medium ${isDark ? 'text-heliona-mint' : 'text-heliona-mint-dark'}`} data-id="element-40">
                Clarity, measurable signal, and a confident scale decision.
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </motion.div>;
}